import { useState, useEffect, useRef } from 'react';
import buildGoogleAutocomplete from './buildGoogleAutocomplete';
import addLocationToAnalytics from './addLocationToAnalytics';
import { requestScriptLoad } from '@components/helpers/externalScriptHelper';
import GeolocationButton from './geolocationButton';

type LocationInputProps = {
  id?: string;
  location?: string;
  name?: string;
  placeholder?: string;
};

type LocationData = {
  location?: string;
  place?: google.maps.GeocoderResult | google.maps.places.PlaceResult;
};

const LocationInput = ({
  name,
  id,
  location,
  placeholder = 'Search by postcode, city etc',
}: LocationInputProps): JSX.Element => {
  const [currentLocation, setCurrentLocation] = useState<string | undefined>(
    location || '',
  );

  const locationInputRef = useRef<HTMLInputElement | null>(null);
  const autocompleteInitialisedRef = useRef<boolean>(false);

  const handleLocationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => setCurrentLocation(event.target.value);

  const updateLocation = ({ place, location }: LocationData): void => {
    const newLocationState = location ?? place?.formatted_address;
    setCurrentLocation(newLocationState);
  };

  const handlePlaceSelected = (place: google.maps.places.PlaceResult): void => {
    addLocationToAnalytics(place);
    updateLocation({ place });
  };

  const initialiseLocationInput = (): void => {
    const existingElement = id ? document.getElementById(id) : null;
    const existingInput =
      existingElement instanceof HTMLInputElement ? existingElement : null;
    const inputElement = existingInput || locationInputRef.current;

    if (
      !window.google?.maps?.places?.Autocomplete ||
      !inputElement ||
      autocompleteInitialisedRef.current
    ) {
      return;
    }

    autocompleteInitialisedRef.current = true;

    try {
      buildGoogleAutocomplete({
        inputElement,
        isFullAddress: false,
        onFinished: () => {},
        onPlaceChange: handlePlaceSelected,
      });
    } catch {
      autocompleteInitialisedRef.current = false;
    }
  };

  useEffect(() => {
    if (window.google?.maps?.places?.Autocomplete) {
      initialiseLocationInput();
      return;
    }

    requestScriptLoad('google', initialiseLocationInput);
  }, [id]);

  const handleGeolocationSuccess = (locationData: LocationData): void => {
    if (!locationData) return;
    updateLocation(locationData);
  };

  return (
    <div className="ch-mb--2">
      <input
        className="ch-form__control ch-mb--2"
        id={id}
        name={name}
        onChange={handleLocationChange}
        placeholder={placeholder}
        ref={locationInputRef}
        type="text"
        value={currentLocation}
      />
      <GeolocationButton
        onSuccess={handleGeolocationSuccess}
        source="find a dealer"
      />
    </div>
  );
};

export default LocationInput;
